import React, { useState, useEffect } from 'react';

import { serviceTypeService } from '../../services/serviceTypeService';
import { ServiceType } from '../../types/barber.types';
import Modal from '../../components/common/Modal';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

const ServiceTypeManagement: React.FC = () => {
    const { showToast, showConfirm } = useToast();
    const [services, setServices] = useState<ServiceType[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [suggestedPrice, setSuggestedPrice] = useState<number>(0);
    const [defaultDuration, setDefaultDuration] = useState<number>(30);
    const [allowParallel, setAllowParallel] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadServices();
    }, []);

    const loadServices = async () => {
        try {
            setLoading(true);
            const data = await serviceTypeService.getAllServices();
            setServices(data);
            setError(null);
        } catch (err) {
            console.error('Error loading services:', err);
            setError('Error al cargar la lista de servicios');
            showToast('No se pudieron cargar los servicios', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenEdit = (service: ServiceType) => {
        setEditingId(service.id);
        setName(service.name);
        setDescription(service.description);
        setSuggestedPrice(service.suggestedPrice);
        setDefaultDuration(service.defaultDurationMinutes);
        setAllowParallel(service.allowParallel);
        setIsModalOpen(true);
    };

    const handleSaveService = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const serviceData = {
                name,
                description,
                suggestedPrice,
                defaultDurationMinutes: defaultDuration,
                allowParallel
            };

            if (editingId) {
                await serviceTypeService.updateService(editingId, serviceData);
                showToast('Servicio actualizado correctamente', 'success');
            } else {
                await serviceTypeService.createService(serviceData);
                showToast('Servicio creado correctamente', 'success');
            }

            setIsModalOpen(false);
            resetForm();
            loadServices();
        } catch (err) {
            console.error('Error saving service:', err);
            showToast('Ocurrió un error al guardar el servicio', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteService = () => {
        if (!editingId) return;

        showConfirm(
            `¿Está seguro que desea eliminar el servicio "${name}"?`,
            async () => {
                setSubmitting(true);
                try {
                    await serviceTypeService.deleteService(editingId);
                    showToast('Servicio eliminado correctamente', 'success');
                    setIsModalOpen(false);
                    resetForm();
                    loadServices();
                } catch (err) {
                    console.error('Error deleting service:', err);
                    showToast('Ocurrió un error al eliminar el servicio', 'error');
                } finally {
                    setSubmitting(false);
                }
            }
        );
    };

    const resetForm = () => {
        setEditingId(null);
        setName('');
        setDescription('');
        setSuggestedPrice(0);
        setDefaultDuration(30);
        setAllowParallel(false);
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS'
        }).format(price);
    };

    return (
        <>
            <AdminHeader
                title="🏷️ Catálogo de Servicios"
                rightContent={
                    <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
                        ➕ Nuevo Servicio
                    </button>
                }
            />
            <div className="container fade-in">

                {loading ? (
                    <div className="card p-xl text-center">
                        <div className="spinner m-auto mb-md"></div>
                        <p>Cargando catálogo...</p>
                    </div>
                ) : error ? (
                    <div className="card p-xl text-center border-error bg-error-light">
                        <p className="text-error mb-md">{error}</p>
                        <button className="btn btn-outline btn-sm" onClick={loadServices}>Reintentar</button>
                    </div>
                ) : services.length === 0 ? (
                    <div className="card p-xl text-center">
                        <p className="text-secondary">No hay tipos de servicio definidos.</p>
                    </div>
                ) : (
                    <div className="responsive-grid">
                        {services.map((service) => (
                            <div key={service.id} className="card p-md shadow-sm border-l-4" style={{ borderLeftColor: 'var(--accent)' }}>
                                <div className="flex justify-between items-start mb-sm">
                                    <h3 className="m-0 font-bold">{service.name}</h3>
                                    <span className={`status-badge ${service.allowParallel ? 'status-active' : 'status-inactive'}`} style={{ fontSize: '0.65rem' }}>
                                        {service.allowParallel ? 'Paralelo' : 'Normal'}
                                    </span>
                                </div>
                                <p className="text-sm text-secondary mb-md line-clamp-2">{service.description || 'Sin descripción'}</p>
                                <div className="flex justify-between items-center bg-gray-50 p-sm rounded-lg mb-md">
                                    <div>
                                        <div className="text-xs text-secondary font-bold uppercase">Precio</div>
                                        <div className="font-bold text-accent">{formatPrice(service.suggestedPrice)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs text-secondary font-bold uppercase">Duración</div>
                                        <div className="font-bold">{service.defaultDurationMinutes} min</div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-primary btn-sm w-full"
                                    onClick={() => handleOpenEdit(service)}
                                >
                                    ✏️ Editar Servicio
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => { setIsModalOpen(false); resetForm(); }}
                    title={editingId ? "Editar Servicio" : "Nuevo Servicio"}
                >
                    <form onSubmit={handleSaveService}>
                        <div className="form-group mb-md">
                            <label className="form-label">Nombre del Servicio *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ej: Corte Clásico"
                                required
                            />
                        </div>

                        <div className="form-group mb-md">
                            <label className="form-label">Descripción</label>
                            <textarea
                                className="form-textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Breve descripción del servicio..."
                                rows={3}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-md mb-md">
                            <div className="form-group">
                                <label className="form-label">Precio Sugerido *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={suggestedPrice}
                                    onChange={(e) => setSuggestedPrice(parseFloat(e.target.value) || 0)}
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Duración (min) *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={defaultDuration}
                                    onChange={(e) => setDefaultDuration(parseInt(e.target.value) || 0)}
                                    min="5"
                                    step="5"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group mb-lg bg-gray-50 p-md rounded-lg border border-dashed border-gray-300">
                            <label className="flex items-center gap-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={allowParallel}
                                    onChange={(e) => setAllowParallel(e.target.checked)}
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <div>
                                    <span className="font-bold block">Trabajo en Paralelo</span>
                                    <span className="text-xs text-secondary">
                                        Permite superponer turnos mientras este está en curso.
                                    </span>
                                </div>
                            </label>
                        </div>

                        <div className="flex flex-col-reverse md:flex-row justify-between items-center gap-sm mt-lg pt-md border-t">
                            <div className="flex flex-col-reverse md:flex-row gap-sm w-full md:w-auto">
                                <button type="button" className="btn btn-outline w-full md:w-auto" onClick={() => { setIsModalOpen(false); resetForm(); }} disabled={submitting}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary w-full md:w-auto" disabled={submitting}>
                                    {submitting ? 'Guardando...' : (editingId ? 'Actualizar' : 'Guardar')}
                                </button>
                            </div>
                            {editingId && (
                                <button
                                    type="button"
                                    className="btn btn-outline border-error text-error hover:bg-error hover:text-white w-full md:w-auto"
                                    onClick={handleDeleteService}
                                    disabled={submitting}
                                    title="Eliminar Servicio"
                                >
                                    🗑️ Eliminar
                                </button>
                            )}
                        </div>
                    </form>
                </Modal>
            </div>
        </>
    );
};

export default ServiceTypeManagement;

